import React, { createRef, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { getPlanName } from '../../../../core/components';
import Price from '../../../../core/components/price';
import Tax from '../../../../core/components/tax';
import { selectLocationDetails, selectPlans, selectUser } from '../../../../../store/features/system';
import { Discount } from '../../../../../common/money/discounts';
import { API } from '../../../../../vendor/api';
import { routerHelperFactory } from '../../../../../global/common/services/route-helper-factory';
import { getUserCurrency } from '../../../helpers';
import { createClinicAccountAndLogin } from '../../../actions';
import { SeatsSelector } from './SeatsSelector';
import { SeatsDescription } from './SeatsDescription';
import { PromoCodeForm } from './PromoCodeForm';
import { SignUpStepper } from '../common/SignUpStepper';
import { SubscriptionTabs } from './SubscriptionTabs';
import { PaymentForm } from './PaymentForm';
import { PaymentButtons } from './PaymentButtons';
import { Loader } from '../common/Loader';
import ArrowBack from '../common/arrow-back.svg';
import '../payment-setup.scss';
const marketingUrl = routerHelperFactory.getBaseUrl();
const STORAGE_KEY_PLAN_INTERVAL = 'typeFromPricingPage';
const STORAGE_KEY_USER_DATA = 'clinicSignUpData';
export var PAYMENT_INTERVAL;
(function (PAYMENT_INTERVAL) {
    PAYMENT_INTERVAL["ANNUAL"] = "yearly";
    PAYMENT_INTERVAL["MONTHLY"] = "monthly";
})(PAYMENT_INTERVAL || (PAYMENT_INTERVAL = {}));
function applyDollarDiscount(subtotal, couponDiscount) {
    const subtotalInCents = subtotal * 100;
    return ((subtotalInCents - new Discount(couponDiscount).apply(subtotalInCents)) /
        100);
}
function getPlanIntervalFromStorage() {
    const planInterval = (sessionStorage.getItem(STORAGE_KEY_PLAN_INTERVAL) ||
        localStorage.getItem(STORAGE_KEY_PLAN_INTERVAL));
    return planInterval === 'yearly' ? PAYMENT_INTERVAL.ANNUAL : PAYMENT_INTERVAL.MONTHLY;
}
function getUserDataFromStorage() {
    const savedData = sessionStorage.getItem(STORAGE_KEY_USER_DATA);
    if (savedData) {
        try {
            return JSON.parse(savedData);
        }
        catch (error) {
            return null;
        }
    }
    return null;
}
export const ClinicPaymentSetup = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const captchaRef = createRef();
    const allPlans = useSelector(selectPlans); // Maybe worth replacing with a direct API call
    const locationDetails = useSelector(selectLocationDetails);
    const currentUser = useSelector(selectUser) || getUserDataFromStorage();
    const [planInterval, setPlanInterval] = useState(getPlanIntervalFromStorage());
    const [currencyCode, setCurrencyCode] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [seats, setSeats] = useState(1);
    const [appliedCoupon, setAppliedCoupon] = useState('');
    const [couponDiscount, setCouponDiscount] = useState('');
    const [countryCode, setCountryCode] = useState(null);
    const [postalCode, setPostalCode] = useState(null);
    const [cardToken, setCardToken] = useState(null);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [taxAmount, setTaxAmount] = useState(0);
    const [isTaxInfoComplete, setIsTaxInfoComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loaderStatus, setLoaderStatus] = useState(null);
    // Saving user data to local storage (in case of page refresh)
    useEffect(() => {
        if (currentUser) {
            sessionStorage.setItem(STORAGE_KEY_USER_DATA, JSON.stringify(currentUser));
        }
    }, [currentUser]);
    // Setting up currency and plan
    useEffect(() => {
        const fetchCurrency = async () => {
            const userCurrency = await getUserCurrency(locationDetails);
            setCurrencyCode(userCurrency);
        };
        void fetchCurrency();
    }, [locationDetails]);
    useEffect(() => {
        if (!currencyCode)
            return;
        const planName = getPlanName(`clinic_${planInterval}-v2`, currencyCode);
        if (allPlans.length > 0) {
            const plan = allPlans.find((p) => p.id === planName || p.stripePlan === planName);
            setSelectedPlan(plan ?? null);
        }
    }, [currencyCode, allPlans, planInterval]);
    const selectedProduct = selectedPlan ? selectedPlan.product : '';
    const basePrice = selectedPlan ? selectedPlan.pricePerSeat / 100 : 0;
    const subtotal = basePrice * seats;
    const discount = applyDollarDiscount(subtotal, couponDiscount);
    const totalWithTax = Math.round((subtotal - discount + taxAmount + Number.EPSILON) * 100) / 100;
    const handleBack = useCallback(() => {
        history.push('/sign-up?accountType=clinic');
    }, [history]);
    const handleCouponApply = useCallback((couponCode, discount) => {
        setAppliedCoupon(couponCode);
        setCouponDiscount(discount);
    }, []);
    const handleCouponRemove = useCallback(() => {
        setAppliedCoupon('');
        setCouponDiscount('');
    }, []);
    const handleIntervalChange = useCallback((newInterval) => {
        setPlanInterval(newInterval);
        handleCouponRemove();
    }, []);
    const handlePaymentDataChange = useCallback(async ({ postalCode, cardToken }) => {
        if (cardToken?.card) {
            setPostalCode(postalCode);
            setCountryCode(cardToken.card.country);
            setCardToken(cardToken.id);
        }
        else {
            setPostalCode(null);
            setCountryCode(null);
            setCardToken(null);
        }
    }, []);
    const isReadyForSubmit = useCallback(() => {
        return Boolean(cardToken && countryCode && isTaxInfoComplete && recaptchaToken);
    }, [cardToken, countryCode, isTaxInfoComplete, recaptchaToken]);
    const handleSubmit = async () => {
        if (!isReadyForSubmit()) {
            return;
        }
        setIsLoading(true);
        setLoaderStatus('pending');
        try {
            const result = await API.account.clinicSignup({
                user: {
                    email: currentUser.email,
                    title: currentUser.title,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    password: currentUser.password,
                },
                institution: {
                    name: currentUser.clinicName,
                    domain: currentUser.clinicDomain,
                    roomName: currentUser.roomName,
                },
                payment: {
                    postalCode,
                    countryCode,
                    planId: selectedPlan.id,
                    seats,
                    cardToken,
                    coupon: appliedCoupon,
                    currency: currencyCode,
                    totalWithTax
                },
                recaptchaToken,
            });
            setLoaderStatus('success');
            localStorage.removeItem(STORAGE_KEY_PLAN_INTERVAL);
            sessionStorage.removeItem(STORAGE_KEY_PLAN_INTERVAL);
            void dispatch(createClinicAccountAndLogin(result.accessToken, result.user, result.institution));
        }
        catch (error) {
            console.error(`Error during clinic signup: ${error}`);
            setLoaderStatus('failed');
        }
        finally {
            setIsLoading(false);
            captchaRef.current?.reset();
        }
    };
    if (loaderStatus) {
        return (<Loader onBack={handleBack} status={loaderStatus}/>);
    }
    return (<div className="sign-up__container">
      <section className="sign-up__section content-box box-vertical scrollable">
        {/* Back button */}
        <a className="sign-up__back-button" onClick={handleBack}>
          <ArrowBack className="sign-up__icon-arrow-btn"/>
          <span className="sign-up__back-button-text">
            <FormattedMessage id="auth.sign-up.goBackUrl"/>
          </span>
        </a>

        {/* Logo header */}
        <header className="logo-header">
          <a className="logo-header-link" href={marketingUrl} rel="noreferer noopener">
            <img className="logo-header-value" src="/assets/images/icons/logo-horizontal.png" alt="logo header"/>
          </a>
        </header>

        {/* Main content */}
        <div className="content">
          <SignUpStepper steps={['auth.sign-up.details', 'auth.sign-up.payment']} currentStepIndex={1}/>

          <SubscriptionTabs planInterval={planInterval} onIntervalChange={handleIntervalChange}/>

          <div className="payment-setup__seats-container">
            <SeatsDescription planInterval={planInterval} basePrice={basePrice} currencyCode={currencyCode}/>

            <SeatsSelector value={seats} onChange={setSeats}/>
          </div>

          <div className="payment-setup__yearly-subscription-container">
            <div>
              <FormattedMessage id="auth.payment-setup.subtotal"/>
            </div>
            <div>
              <Price item={subtotal} currency={currencyCode} showDecimals={true}/>
            </div>
          </div>

          <div className="payment-setup__discount-container">
            <div className="payment-setup__discount-label">
              <div>
                <FormattedMessage id="auth.payment-setup.discount"/>
              </div>
            </div>
            <div>
              -
              <Price item={discount} currency={currencyCode} showDecimals={true}/>
            </div>
          </div>

          <PromoCodeForm key={planInterval} // Reset promo code form when plan interval changes
     currencyCode={currencyCode} productId={selectedProduct} onCouponApply={handleCouponApply} onCouponRemove={handleCouponRemove} amount={subtotal * 100} // amount in cents
    />

          <div className="payment-setup__todays-payment-container">
            <div className="payment-setup-todays-payment-label">
              <FormattedMessage id="auth.payment-setup.payment-amount"/>
            </div>
            <div className="payment-setup-todays-payment-value">
              <Price item={subtotal - discount} currency={currencyCode} showDecimals={true}/>
            </div>
          </div>

          <div className="payment-setup__tax-container">
            <Tax email={currentUser.email} planName={selectedPlan?.stripePlan} postalCode={postalCode} countryCode={countryCode} coupon={appliedCoupon} seats={seats} isValid={setIsTaxInfoComplete} getAmount={setTaxAmount}/>
          </div>

          <div className="payment-setup__break"></div>

          <PaymentForm captchaRef={captchaRef} onChange={handlePaymentDataChange} onRecaptchaTokenChange={setRecaptchaToken} onSubmit={handleSubmit}/>

          <PaymentButtons onBack={handleBack} onPay={handleSubmit} isDisabled={!isReadyForSubmit()} isLoading={isLoading} totalWithTax={totalWithTax} currencyCode={currencyCode}/>
        </div>
      </section>
    </div>);
};
